import * as React from 'react';
import { PaginatedTable } from '@teamsnap/teamsnap-ui';
import { useQuery } from '@apollo/client';
import * as ShowMembers from '../../core/gql/queries/show-members';
import Member from '../../models/member';
import MergeMembersModal from './merge-members-modal';
import { Link } from 'react-router-dom';
import { uniqWith, isEqual } from 'lodash';
import { displayLastNameFirst } from 'core/utils';
import { Placement } from '@teamsnap/teamsnap-ui/dist/js/types/placement';

export interface Props {
  orgId: number;
}

const defaultPageSize = 50;

const MemberList: React.FunctionComponent<Props> = ({ orgId }) => {
  const { data, refetch } = useQuery(ShowMembers.QUERY, {
    variables: { organizationId: orgId, pageNumber: 1, pageSize: defaultPageSize, searchTerm: '' },
  });

  const [mergeMembers, setMergeMembers] = React.useState<Member[]>([]);
  const [forceReload, setForceReload] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (forceReload) {
      setForceReload(false);
    }
  }, [forceReload]);

  const columns = [
    { name: 'name', label: 'Member Name', isSortable: false, mods: 'u-size1of2' },
    { name: 'gender', label: 'Gender', isSortable: false, mods: 'u-size1of2' },
    { name: 'birthdate', label: 'Birthdate', isSortable: false, mods: 'u-size1of2' },
    { name: 'activePrograms', label: 'Active Programs', isSortable: false, mods: 'u-size1of2' },
  ];

  function loadData({ page, itemsPerPage, sortBy, sortAsc, filter }) {
    return refetch({
      organizationId: orgId,
      pageNumber: page,
      pageSize: itemsPerPage,
      searchTerm: filter.searchTerm ? filter.searchTerm.trim() : '',
    }).then((val) => ({
      data: val.data.members.data,
      totalItems: val.data.members.metadata.totalEntries,
    }));
  }

  function mapData(item: Member, index: number) {
    const uniqueParticipantSeasons = uniqWith(
      item.seasonParticipants.map((m) => m.season),
      isEqual
    );

    return {
      id: item.id,
      name: (
        <div>
          <Link to={`/organizations/${orgId}/members/${item.id}`}>{displayLastNameFirst(item)}</Link>
        </div>
      ),
      activePrograms:
        item.seasonParticipants.length == 0 ? (
          '-'
        ) : (
          <div>
            {uniqueParticipantSeasons.map((e) => (
              <div key={e.program.name}>
                {e.program.name} <span className="u-colorGrey">({e.name})</span>
              </div>
            ))}
          </div>
        ),
      firstName: item.firstName,
      lastName: item.lastName,
      insertedAt: item.insertedAt,
      gender: item.gender,
      birthdate: item.birthdate,
    };
  }

  return (
    <>
      <div className="u-padBottomXxl">
        {forceReload || !data ? null : (
          <PaginatedTable
            loadData={loadData}
            mapDataToRow={mapData}
            columns={columns}
            bulkActions={[
              {
                label: 'Merge members',
                onSelected: (selected) => {
                  setMergeMembers(selected);
                },
              },
            ]}
            rowsAreSelectable={true}
            defaultItemsPerPage={defaultPageSize}
            hideRowsSelect={true}
            includeBasicSearch={true}
            searchPlaceholder="Seach by member name..."
            paginationPlacement={Placement.RightBottom}
          />
        )}
      </div>
      <MergeMembersModal
        members={mergeMembers}
        isOpen={mergeMembers.length > 0}
        onMergeSuccess={() => {
          setMergeMembers([]);
          setForceReload(true);
        }}
        onClose={() => setMergeMembers([])}
        orgId={orgId}
      />
    </>
  );
};

export default MemberList;
